<template>
  <div class="agent-edit-view">
    <!-- Header avec titre dynamique et bouton de retour personnalisé -->
    <Header :title="pageTitle">
      <template #icon>
        <span class="material-icons header-icon">person_add</span>
      </template>
      <template #actions>
        <button @click="goBack" class="btn-return">
          <span class="material-icons">arrow_back</span>
          Retour à la liste
        </button>
      </template>
    </Header>
    
    <div class="container py-5">
      <div class="edit-card">
        <div class="edit-header" :class="{ 'new-mode': !agent.matricule }">
          <div class="header-content">
            <p class="edit-subtitle">
              {{ agent.matricule ? `Matricule: ${agent.matricule}` : 'Veuillez remplir les informations du personnel' }}
            </p>
          </div>
        </div>
        
        <div class="edit-body">
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <span class="loading-text">Chargement des informations...</span>
          </div>
          
          <form v-else @submit.prevent="saveAgent" class="agent-form">
            <div class="form-layout">
              <!-- Photo de l'agent -->
              <div class="photo-section">
                <div class="photo-container">
                  <img 
                    :src="getPhotoUrl(agent.photo)" 
                    class="agent-preview"
                    alt="Photo de l'agent"
                    @error="handleImageError"
                  />
                </div>
                
                <div class="photo-actions">
                  <button type="button" class="photo-action-btn" @click="openPhotoModal" title="Sélectionner un fichier">
                    <span class="material-icons">photo_library</span>
                    <span>Sélectionner</span>
                  </button>
                  <!-- <button type="button" class="photo-action-btn" @click="openPhotoModal" title="Prendre une photo">
                    <span class="material-icons">photo_camera</span>
                    <span>Capturer</span>
                  </button> -->
                  <input 
                    type="file" 
                    ref="fileInput" 
                    @change="handleFileSelect" 
                    accept="image/*" 
                    style="display: none"
                  />
                </div>
                <div class="photo-hint">Format portrait recommandé</div>
              </div>
                
              <!-- Contenu principal du formulaire -->
              <div class="form-content">
                <!-- Informations personnelles -->
                <div class="form-section">
                  <div class="section-header">
                    <span class="material-icons section-icon">person</span>
                    <h4 class="section-title">Informations personnelles</h4>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="matricule">Matricule *</label>
                        <input 
                          type="text" 
                          id="matricule" 
                          v-model="agent.matricule" 
                          placeholder="Saisir le matricule"
                          required
                        >
                        <div class="error-message" v-if="showValidation && !agent.matricule">Ce champ est obligatoire</div>
                      </div>
                    </div>
                    
                    <div class="form-column">
                      <div class="form-field">
                        <label for="nom" class="required-field">Nom complet *</label>
                        <input 
                          type="text" 
                          id="nom" 
                          v-model="agent.nom"
                          placeholder="Saisir le nom complet"
                          class="form-control"
                          :class="{ 'is-invalid': showValidation && !agent.nom }"
                          required
                        >
                        <div class="error-message" v-if="showValidation && !agent.nom">Ce champ est obligatoire</div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="sexe" class="required-field">Sexe</label>
                        <select 
                          id="sexe" 
                          v-model="agent.sexe"
                          class="form-control"
                          :class="{ 'is-invalid': showValidation && !agent.sexe }"
                          required
                        >
                          <option value="" disabled>Sélectionner</option>
                          <option value="M">Homme</option>
                          <option value="F">Femme</option>
                        </select>
                        <div class="error-message" v-if="showValidation && !agent.sexe">Ce champ est obligatoire</div>
                      </div>
                    </div>
                    
                    <div class="form-column">
                      <div class="form-field">
                        <label for="date_naissance">Date de naissance</label>
                        <input 
                          type="date" 
                          id="date_naissance" 
                          v-model="agent.date_naissance"
                        >
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column full-width">
                      <div class="form-field">
                        <label for="lieu_naissance">Lieu de naissance</label>
                        <input 
                          type="text" 
                          id="lieu_naissance" 
                          v-model="agent.lieu_naissance" 
                          placeholder="Saisir le lieu de naissance"
                        >
                      </div>
                    </div>
                  </div>
                </div>
              
                <!-- Informations professionnelles -->
                <div class="form-section">
                  <div class="section-header">
                    <span class="material-icons section-icon">business_center</span>
                    <h4 class="section-title">Informations professionnelles</h4>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="direction" class="required-field">Direction</label>
                        <select 
                          id="direction" 
                          v-model="agent.direction_id"
                          class="form-control select2-direction"
                          :class="{ 'is-invalid': showValidation && !agent.direction_id }"
                          data-placeholder="Sélectionner une direction"
                          required
                        >
                          <option value="" disabled>Sélectionner une direction</option>
                          <option v-for="direction in directionsData" :key="direction.id" :value="direction.id">
                            {{ direction.nom }}
                          </option>
                        </select>
                        <div class="error-message" v-if="showValidation && !agent.direction_id">Ce champ est obligatoire</div>
                      </div>
                    </div>
                    
                    <div class="form-column">
                      <div class="form-field">
                        <label for="grade">Grade</label>
                        <select 
                          id="grade" 
                          v-model="agent.grade_id" 
                          class="form-control select2-grade"
                          data-placeholder="Sélectionner un grade"
                        >
                          <option value="" disabled>Sélectionner un grade</option>
                          <option v-for="grade in grades" :key="grade.id" :value="grade.id">
                            {{ grade.libelle || grade.description }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="fonction">Fonction</label>
                        <select 
                          id="fonction" 
                          v-model="agent.fonction_id" 
                          class="form-control select2-fonction"
                          data-placeholder="Sélectionner une fonction"
                        >
                          <option value="" disabled>Sélectionner une fonction</option>
                          <option v-for="fonction in fonctions" :key="fonction.id" :value="fonction.id">
                            {{ fonction.libelle || fonction.description }}
                          </option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="form-column">
                      <div class="form-field">
                        <label for="date_recrutement">Date de recrutement</label>
                        <input 
                          type="date" 
                          id="date_recrutement" 
                          v-model="agent.date_recrutement"
                        >
                      </div>
                    </div>
                  </div>
                  

                  

                  
                  <!-- Champ statut -->
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="statut">Statut</label>
                        <select 
                          id="statut" 
                          v-model="agent.statut"
                          class="form-control select2-statut"
                          data-placeholder="Sélectionner un statut"
                        >
                          <option value="actif">Actif</option>
                          <option value="inactif">Inactif</option>
                          <option value="conge">En congé</option>
                          <option value="formation">En formation</option>
                          <option value="detachement">En détachement</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Informations sociales -->
                <div class="form-section">
                  <div class="section-header">
                    <span class="material-icons section-icon">people</span>
                    <h4 class="section-title">Informations sociales</h4>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="etat_civil">État civil</label>
                        <select 
                          id="etat_civil" 
                          v-model="agent.etat_civil"
                          class="form-control"
                        >
                          <option value="">Sélectionner</option>
                          <option value="celibataire">Célibataire</option>
                          <option value="marie">Marié(e)</option>
                          <option value="divorce">Divorcé(e)</option>
                          <option value="veuf">Veuf(ve)</option>
                          <option value="pacse">Pacsé(e)</option>
                          <option value="concubinage">Concubinage</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="form-column" v-if="agent.etat_civil === 'marie'">
                      <div class="form-field">
                        <label for="nom_conjoint">Nom du conjoint</label>
                        <input 
                          type="text" 
                          id="nom_conjoint" 
                          v-model="agent.nom_conjoint" 
                          placeholder="Nom complet du conjoint"
                        >
                      </div>
                    </div>
                    
                    <div class="form-column" v-if="agent.etat_civil === 'marie'">
                      <div class="form-field">
                        <label for="contact_conjoint">Contact du conjoint</label>
                        <input 
                          type="tel" 
                          id="contact_conjoint" 
                          v-model="agent.contact_conjoint" 
                          placeholder="Téléphone du conjoint"
                        >
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="nombre_enfants">Nombre d'enfants</label>
                        <input 
                          type="number" 
                          id="nombre_enfants" 
                          v-model.number="agent.nombre_enfants" 
                          min="0"
                          placeholder="0"
                          @input="syncNomsEnfantsLength"
                        >
                        <div class="field-hint">Le nombre de noms complets ci‑dessous doit être égal à ce nombre.</div>
                      </div>
                    </div>
                    
                    <div class="form-column">
                      <div class="form-field">
                        <label for="contact_urgence">Contact d'urgence</label>
                        <input 
                          type="tel" 
                          id="contact_urgence" 
                          v-model="agent.contact_urgence" 
                          placeholder="Téléphone en cas d'urgence"
                        >
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row" v-if="agent.nombre_enfants > 0">
                    <div class="form-column full-width">
                      <div class="form-field">
                        <label>Noms complets des enfants ({{ agent.noms_enfants.length }} / {{ agent.nombre_enfants }})</label>
                        <div class="noms-enfants-inputs">
                          <div 
                            v-for="(nom, index) in agent.noms_enfants" 
                            :key="index" 
                            class="noms-enfants-row"
                          >
                            <input 
                              type="text" 
                              v-model="agent.noms_enfants[index]" 
                              :placeholder="`Enfant ${index + 1} - Nom complet`"
                              class="form-control"
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Pointage mobile -->
                <div class="form-section">
                  <div class="section-header">
                    <span class="material-icons section-icon">fingerprint</span>
                    <h4 class="section-title">Pointage</h4>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="badge_id">ID Badge</label>
                        <input 
                          type="text" 
                          id="badge_id" 
                          v-model="agent.badge_id" 
                          placeholder="Identifiant du badge"
                        >
                        <div class="field-hint">Identifiant unique du badge de pointage</div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column full-width">
                      <div class="form-field toggle-field">
                        <div class="toggle-container">
                          <label class="toggle-label" for="active_mobile">
                            <span class="material-icons toggle-icon">smartphone</span>
                            Activer le pointage mobile
                          </label>
                          <div class="toggle-switch-container">
                            <label class="switch">
                              <input type="checkbox" id="active_mobile" v-model="agent.active">
                              <span class="slider round"></span>
                            </label>
                            <span class="toggle-status">{{ agent.active ? 'Activé' : 'Désactivé' }}</span>
                          </div>
                        </div>
                        <div class="field-hint toggle-hint">
                          <span class="material-icons info-icon">info</span>
                          <span>Si activé, l'agent pourra utiliser l'application mobile pour pointer sa présence par géolocation. Sinon, il devra utiliser les capteurs physiques installés dans le bâtiment.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              
                <!-- Coordonnées -->
                <div class="form-section">
                  <div class="section-header">
                    <span class="material-icons section-icon">contact_phone</span>
                    <h4 class="section-title">Coordonnées</h4>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="email">Email</label>
                        <input 
                          type="email" 
                          id="email" 
                          v-model="agent.email" 
                          placeholder="Saisir l'email"
                        >
                      </div>
                    </div>
                    
                    <div class="form-column">
                      <div class="form-field">
                        <label for="telephone">Téléphone</label>
                        <input 
                          type="tel" 
                          id="telephone" 
                          v-model="agent.telephone" 
                          placeholder="Saisir le numéro de téléphone"
                        >
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column">
                      <div class="form-field">
                        <label for="ville">Ville</label>
                        <select 
                          id="ville" 
                          v-model="agent.ville_id"
                          class="form-control select2-ville"
                          data-placeholder="Sélectionner une ville"
                        >
                          <option value="" disabled>Sélectionner une ville</option>
                          <option v-for="ville in villes" :key="ville.id" :value="ville.id">
                            {{ ville.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="form-column">
                      <div class="form-field">
                        <label for="code_postal">Code postal</label>
                        <input 
                          type="text" 
                          id="code_postal" 
                          v-model="agent.code_postal" 
                          placeholder="Saisir le code postal"
                        >
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-row">
                    <div class="form-column full-width">
                      <div class="form-field">
                        <label for="adresse" class="required-field">Adresse </label>
                        <textarea 
                          id="adresse" 
                          v-model="agent.adresse" 
                          placeholder="Saisir l'adresse complète" 
                          rows="3"
                          required
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="form-actions">
              <button type="button" class="btn-cancel" @click="goBack" :disabled="loading">
                <span class="material-icons">close</span>
                Annuler
              </button>
              <button type="button" class="btn-save" @click="openConfirmSaveModal" :disabled="loading">
                <span class="material-icons" v-if="!loading">save</span>
                <span class="material-icons loading-icon" v-else>sync</span>
                {{ loading ? 'Enregistrement...' : (agent.num_personnel ? 'Mettre à jour' : 'Enregistrer') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Modal de confirmation d'annulation -->
    <dialog ref="confirmCancelModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer l'annulation</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir annuler les modifications ?</p>
        <p class="warning">Les modifications non enregistrées seront perdues.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal">Retour</button>
        <button class="btn btn-danger" @click="confirmCancel">Annuler les modifications</button>
      </div>
    </dialog>
    
    <!-- Modal de confirmation de sauvegarde -->
    <dialog ref="confirmSaveModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer l'enregistrement</h3>
        <button class="btn-close" @click="closeConfirmSaveModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir {{ agent.num_personnel ? 'mettre à jour' : 'créer' }} cet agent ?</p>
        <div class="confirmation-details">
          <p><strong>Nom:</strong> {{ agent.nom }} {{ agent.prenom }}</p>
          <p><strong>Matricule:</strong> {{ agent.matricule || 'Non défini' }}</p>
          <p><strong>Direction:</strong> {{ directionLabel }}</p>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmSaveModal">Annuler</button>
        <button class="btn btn-primary" @click="confirmSave">
          <span class="material-icons">save</span>
          {{ agent.num_personnel ? 'Mettre à jour' : 'Enregistrer' }}
        </button>
      </div>
    </dialog>
    
    <!-- Modal pour la webcam -->
    <div v-if="showCamera" class="camera-modal">
      <div class="camera-modal-content">
        <div class="camera-modal-header">
          <h5>Capture de photo</h5>
          <button @click="showCamera = false" class="close-btn">
            <i class="material-icons">close</i>
          </button>
        </div>
        
        <div class="camera-modal-body">
          <div class="camera-container">
            <video ref="video" autoplay playsinline></video>
            <canvas ref="canvas" style="display: none;"></canvas>
          </div>
          
          <div class="camera-controls">
            <button @click="capturePhoto" class="btn btn-primary">
              <i class="material-icons">photo_camera</i> Capturer
            </button>
            <button v-if="cameraCaptured" @click="acceptPhoto" class="btn btn-success ms-2">
              <i class="material-icons">check</i> Accepter
            </button>
            <button v-if="cameraCaptured" @click="retakePhoto" class="btn btn-secondary ms-2">
              <i class="material-icons">refresh</i> Reprendre
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal pour rogner la photo -->
    <div v-if="showCropper" class="cropper-modal">
      <div class="cropper-modal-content">
        <div class="cropper-modal-header">
          <h5>Rogner la photo</h5>
          <button @click="cancelCrop" class="close-btn">
            <i class="material-icons">close</i>
          </button>
        </div>
        
        <div class="cropper-modal-body">
          <div class="cropper-container">
            <div class="img-container">
              <img ref="cropperImage" :src="cropperImage" alt="Image à rogner" style="max-width: 100%;" />
            </div>
          </div>
          
          <div class="cropper-controls mt-3">
            <button @click="rotateLeft" class="btn btn-outline-secondary me-2">
              <i class="material-icons">rotate_left</i>
            </button>
            <button @click="rotateRight" class="btn btn-outline-secondary me-2">
              <i class="material-icons">rotate_right</i>
            </button>
            <button @click="applyCrop" class="btn btn-primary">
              <i class="material-icons">crop</i> Appliquer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Modal de sélection et aperçu de photo -->
  <dialog ref="photoModal" class="confirm-modal photo-modal">
    <div class="modal-header">
      <h3>Photo de l'agent</h3>
      <button class="btn-close" @click="closePhotoModal">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body scrollable-modal-body">
      <!-- Mode webcam -->
      <div v-if="showCamera" class="webcam-container">
        <video ref="video" autoplay playsinline class="webcam-video"></video>
        <canvas ref="canvas" class="webcam-canvas" style="display: none;"></canvas>
        
        <div class="webcam-controls">
          <button v-if="!cameraCaptured" type="button" class="btn btn-action" @click="capturePhoto">
            <span class="material-icons">photo_camera</span>
            Capturer
          </button>
          <template v-else>
            <button type="button" class="btn btn-secondary" @click="retakePhoto">
              <span class="material-icons">refresh</span>
              Reprendre
            </button>
            <button type="button" class="btn btn-action" @click="acceptPhoto">
              <span class="material-icons">check</span>
              Accepter
            </button>
          </template>
        </div>
      </div>
      
      <!-- Mode aperçu normal -->
      <div v-else>
        <div class="photo-preview-container">
          <img v-if="previewPhoto" :src="previewPhoto" class="photo-preview" alt="Aperçu de la photo" @error="handleImageError" />
          <div v-else class="photo-placeholder modal-placeholder">
            <span class="material-icons">account_circle</span>
            <p>Aucune photo sélectionnée</p>
          </div>
        </div>
        <div class="photo-options">
          <button type="button" class="btn btn-action-outline" @click="triggerFileInputFromModal">
            <span class="material-icons">photo_library</span>
            Sélectionner une image
          </button>
          <button type="button" class="btn btn-action-outline" @click="startWebcam">
            <span class="material-icons">photo_camera</span>
            Prendre une photo
          </button>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" @click="closePhotoModal">Annuler</button>
      <button class="btn btn-action" @click="confirmPhoto">Confirmer</button>
    </div>
  </dialog>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePersonnelStore } from '@/store/personnel'
import api from '@/services/api'
import Cropper from 'cropperjs'
import { useToast } from '@/composables/useToast'
import Header from '@/components/Header.vue'
import $ from 'jquery'
import 'select2'

// Router & Store
const router = useRouter()
const route = useRoute()
const personnelStore = usePersonnelStore()
const { showToast } = useToast()

// État de l'agent et du formulaire
const agent = reactive({
  id: null,
  matricule: '',
  nom: '',
  sexe: '',
  date_naissance: '',
  lieu_naissance: '',
  grade_id: '',
  fonction_id: '',
  direction_id: '',
  date_recrutement: '',
  email: '',
  telephone: '',
  photo: null,
  shifts: [],
  adresse: '',
  code_postal: '',
  ville_id: '',
  default_shift_id: '',
  statut: 'actif',
  active: false,
  etat_civil: '',
  nom_conjoint: '',
  nombre_enfants: 0,
  noms_enfants: [],
  contact_conjoint: '',
  contact_urgence: ''
})

const loading = ref(true)
const photoPreview = ref(null)

// Données de référence
const grades = ref([])
const fonctions = ref([])
const directionsData = ref([])
const villes = ref([])

// Configuration de Select2
const select2Config = {
  width: '100%',
  language: 'fr',
  allowClear: true,
  placeholder: 'Sélectionner...',
  dropdownCssClass: 'select2-dropdown-opsolver'
}

// Références DOM
const video = ref(null)
const canvas = ref(null)
const fileInput = ref(null)
const cropperImage = ref(null)
const confirmCancelModal = ref(null)
const confirmSaveModal = ref(null)
const photoModal = ref(null)

// États réactifs
const showValidation = ref(false)
const select2Initialized = ref(false)
const showCamera = ref(false)
const showCropper = ref(false)
const cameraCaptured = ref(false)

// Propriétés calculées
const pageTitle = computed(() => agent.num_personnel ? 'Modifier l\'agent' : 'Ajouter un nouvel agent')
const directionLabel = computed(() => {
  if (!agent.direction_id) return 'Non définie';
  
  const direction = directionsData.value.find(d => d.id === parseInt(agent.direction_id));
  return direction ? direction.nom : 'Non définie';
})
const stream = ref(null)
const previewPhoto = ref('')

// Instance de cropper
let cropperInstance = null

// Fonctions pour charger les données depuis l'API
// Fonctions pour récupérer les données depuis l'API
const fetchDirections = async () => {
  try {
    const response = await api.getDirections()
    if (response && response.data) {
      directionsData.value = response.data.data || []
      console.log('Directions chargées:', directionsData.value)
    }
  } catch (error) {
    console.error('Erreur lors du chargement des directions:', error)
    showToast({ title: 'Erreur', message: 'Impossible de charger les directions', type: 'error' })
  }
}

const fetchGrades = async () => {
  try {
    const response = await api.getGrades()
    if (response && response.data) {
      grades.value = response.data.data || []
      console.log('Grades chargés:', grades.value)
    }
  } catch (error) {
    console.error('Erreur lors du chargement des grades:', error)
    showToast({ title: 'Erreur', message: 'Impossible de charger les grades', type: 'error' })
  }
}

const fetchFonctions = async () => {
  try {
    const response = await api.getFonctions()
    if (response && response.data) {
      fonctions.value = response.data.data || []
      console.log('Fonctions chargées:', fonctions.value)
    }
  } catch (error) {
    console.error('Erreur lors du chargement des fonctions:', error)
    showToast({ title: 'Erreur', message: 'Impossible de charger les fonctions', type: 'error' })
  }
}



const fetchVilles = async () => {
  try {
    const response = await api.getVilles()
    if (response && response.data) {
      villes.value = response.data.data || []
      console.log('Villes chargées:', villes.value)
    }
  } catch (error) {
    console.error('Erreur lors du chargement des villes:', error)
    showToast({ title: 'Erreur', message: 'Impossible de charger les villes', type: 'error' })
  }
}

// Fonction pour formater l'heure (HH:MM:SS -> HH:MM)
const formatTime = (timeString) => {
  if (!timeString) return ''
  const timeParts = timeString.split(':')
  return `${timeParts[0]}:${timeParts[1]}`
}

// Fonction pour formater la date (YYYY-MM-DD -> DD/MM/YYYY)
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR')
}

// Fonction pour formater une date au format YYYY-MM-DD pour les inputs de type date
const formatDateForInput = (dateValue) => {
  if (!dateValue) return ''
  
  // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue
  }
  
  // Essayer de parser la date
  const date = new Date(dateValue)
  if (!isNaN(date.getTime())) {
    // Formater en YYYY-MM-DD
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  // Si le parsing échoue, retourner une chaîne vide
  return ''
}

// Ajuster la longueur de noms_enfants pour qu'elle soit égale à nombre_enfants
const syncNomsEnfantsLength = () => {
  const n = Math.max(0, parseInt(agent.nombre_enfants, 10) || 0)
  agent.nombre_enfants = n
  if (!Array.isArray(agent.noms_enfants)) {
    agent.noms_enfants = []
  }
  while (agent.noms_enfants.length < n) {
    agent.noms_enfants.push('')
  }
  if (agent.noms_enfants.length > n) {
    agent.noms_enfants.splice(n)
  }
}



// Charger la liste complète des agents pour récupérer date_naissance et date_recrutement
const loadPersonnelList = async () => {
  try {
    const response = await api.get('/api/personnel-liste')
    if (response && response.data) {
      if (response.data.success && response.data.data) {
        return response.data.data
      } else if (Array.isArray(response.data)) {
        return response.data
      } else if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data
      }
    }
    return []
  } catch (err) {
    console.error('Erreur lors du chargement de la liste du personnel:', err)
    return []
  }
}

// Fonction pour initialiser les données de l'agent
const initializeAgent = async () => {
  loading.value = true
  
  try {
    // Charger toutes les données de référence en parallèle
    await Promise.all([
      // Charger les données du personnel si nécessaire
      !personnelStore.personnel || !personnelStore.personnel.data ? personnelStore.fetchPersonnel() : Promise.resolve(),
      // Charger les données de référence
      fetchDirections(),
      fetchGrades(),
      fetchFonctions(),
      fetchVilles()
    ])
    
    const id = route.params.id
    
    // Mode édition
    if (id && id !== 'new') {
      try {
        // Charger la liste AVANT de charger l'agent individuel pour récupérer date_naissance et date_recrutement
        const personnelList = await loadPersonnelList()
        console.log('Liste du personnel chargée pour édition:', personnelList.length, 'agents')
        
        // Charger l'agent depuis l'endpoint individuel pour tous les champs
        const response = await api.get(`/api/personnel/${id}`)
        let agentData = response.data
        
        // Gérer différentes structures de réponse
        if (agentData && agentData.data) {
          agentData = agentData.data
        }
        
        Object.assign(agent, agentData)
        console.log('Agent chargé depuis API individuelle:', {
          id: agent.id,
          num_personnel: agent.num_personnel,
          date_naissance: agent.date_naissance,
          date_recrutement: agent.date_recrutement
        })
        
        // Normaliser noms_enfants (string -> array) et synchroniser avec nombre_enfants
        if (typeof agent.noms_enfants === 'string') {
          try {
            const parsed = JSON.parse(agent.noms_enfants)
            agent.noms_enfants = Array.isArray(parsed) ? parsed : (agent.noms_enfants.trim() ? [agent.noms_enfants.trim()] : [])
          } catch (_) {
            agent.noms_enfants = agent.noms_enfants.split(',').map(s => s.trim()).filter(Boolean)
          }
        }
        if (!Array.isArray(agent.noms_enfants)) {
          agent.noms_enfants = []
        }
        syncNomsEnfantsLength()
        
        // Formater les dates qui viennent directement de agentData
        if (agent.date_naissance) {
          agent.date_naissance = formatDateForInput(agent.date_naissance)
          console.log('Date de naissance formatée depuis agentData:', agent.date_naissance)
        }
        
        if (agent.date_recrutement) {
          agent.date_recrutement = formatDateForInput(agent.date_recrutement)
          console.log('Date de recrutement formatée depuis agentData:', agent.date_recrutement)
        } else if (agent.date_embauche) {
          agent.date_recrutement = formatDateForInput(agent.date_embauche)
          console.log('Date d\'embauche formatée depuis agentData:', agent.date_recrutement)
        }
        
        // Chercher l'agent dans la liste pour extraire date_naissance et date_recrutement
        if (personnelList && personnelList.length > 0) {
          const agentFromList = personnelList.find(a => {
            // Essayer plusieurs méthodes de correspondance
            const matchById = (a.id === parseInt(id) || a.id === id || String(a.id) === String(id))
            const matchByNumPersonnel = (a.num_personnel === parseInt(id) || a.num_personnel === id || String(a.num_personnel) === String(id))
            const matchByAgentId = (agentData.num_personnel && (a.num_personnel === agentData.num_personnel || a.id === agentData.num_personnel))
            const matchByAgentNumPersonnel = (agentData.id && (a.id === agentData.id || a.num_personnel === agentData.id))
            const matchByMatricule = (agentData.matricule && a.matricule === agentData.matricule)
            
            return matchById || matchByNumPersonnel || matchByAgentId || matchByAgentNumPersonnel || matchByMatricule
          })
          
          console.log('Agent trouvé dans la liste:', agentFromList ? {
            id: agentFromList.id,
            num_personnel: agentFromList.num_personnel,
            date_naissance: agentFromList.date_naissance,
            date_recrutement: agentFromList.date_recrutement,
            date_embauche: agentFromList.date_embauche
          } : 'Non trouvé')
          
          // Extraire seulement date_naissance et date_recrutement depuis la liste
          // (priorité à la liste si les dates n'existent pas dans agentData)
          if (agentFromList) {
            // Formater date_naissance pour l'input de type date (YYYY-MM-DD)
            if (agentFromList.date_naissance && !agent.date_naissance) {
              agent.date_naissance = formatDateForInput(agentFromList.date_naissance)
              console.log('Date de naissance extraite depuis la liste:', agent.date_naissance)
            }
            
            // Formater date_recrutement ou date_embauche pour l'input de type date (YYYY-MM-DD)
            if (agentFromList.date_recrutement && !agent.date_recrutement) {
              agent.date_recrutement = formatDateForInput(agentFromList.date_recrutement)
              console.log('Date de recrutement extraite depuis la liste:', agent.date_recrutement)
            } else if (agentFromList.date_embauche && !agent.date_recrutement) {
              agent.date_recrutement = formatDateForInput(agentFromList.date_embauche)
              console.log('Date d\'embauche extraite depuis la liste:', agent.date_recrutement)
            }
          } else {
            console.warn('Agent non trouvé dans la liste pour extraire les dates')
          }
        } else {
          console.warn('Liste du personnel vide ou non chargée')
        }
        
        console.log('Données finales de l\'agent après combinaison:', {
          date_naissance: agent.date_naissance,
          date_recrutement: agent.date_recrutement
        })
        
        // Prévisualisation de la photo
        if (agent.photo) {
          photoPreview.value = agent.photo
        }
        
        // Les shifts ont été supprimés
      } catch (error) {
        console.error('Erreur lors de la récupération des données de l\'agent', error)
        showToast({ title: 'Erreur', message: 'Impossible de charger les données de l\'agent', type: 'error' })
      }
    } else {
      // Mode création : initialiser avec des valeurs par défaut si nécessaire
      Object.assign(agent, {
        matricule: '',
        nom: '',
        sexe: '',
        date_naissance: '',
        lieu_naissance: '',
        direction_id: '',
        grade_id: '',
        fonction_id: '',
        shifts: [],
        date_recrutement: '',
        email: '',
        telephone: '',
        adresse: '',
        code_postal: '',
        ville_id: '',
        default_shift_id: '',
        statut: 'actif',
        site_pointage_id: '',
        etat_civil: '',
        nom_conjoint: '',
        nombre_enfants: 0,
        noms_enfants: [],
        contact_conjoint: '',
        contact_urgence: ''
      })
      photoPreview.value = null
    }
  
    // Initialiser la webcam si disponible
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const constraints = {
          video: true
        }
        stream.value = await navigator.mediaDevices.getUserMedia(constraints)
        if (video.value) {
          video.value.srcObject = stream.value
          video.value.play()
        }
      } catch (err) {
        console.error("Erreur d'accès à la webcam:", err)
      }
    }
    
    // Initialiser Select2 après le chargement complet du DOM et des données
    nextTick(() => {
      // Attendre un court instant pour s'assurer que le DOM est complètement rendu
      setTimeout(() => {
        if (!select2Initialized.value) {
          initializeSelect2();
          select2Initialized.value = true;
        }
      }, 300);
    });
    
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error);
    showToast({ title: 'Erreur', message: 'Impossible de charger les données nécessaires', type: 'error' })
  } finally {
    loading.value = false
  }
}

// Exécuter l'initialisation au montage du composant
onMounted(() => {
  initializeAgent()
})

// Nettoyer les ressources lorsqu'on quitte le composant
onUnmounted(() => {
  // Arrêter la webcam
  stopWebcam();
  
  // Détruire l'instance du cropper
  if (cropperInstance) {
    cropperInstance.destroy();
    cropperInstance = null;
  }
  
  // Détruire les instances Select2
  try {
    $('#shifts').select2('destroy');
  } catch (error) {
    console.error('Erreur lors de la destruction des instances Select2:', error);
  }
});

// Configuration pour les instances Select2
const select2Options = {
  width: '100%',
  theme: 'classic',
  dropdownCssClass: 'select2-dropdown-opsolver',
  selectionCssClass: 'select2-selection-opsolver',
  allowClear: true,
  closeOnSelect: false, // Pour les selects multiples
  // Activer la recherche
  minimumInputLength: 0,
  minimumResultsForSearch: 5, // Afficher la recherche si plus de 5 options
  language: {
    noResults: () => 'Aucun résultat trouvé',
    searching: () => 'Recherche en cours...',
    inputTooShort: (args) => `Veuillez saisir au moins ${args.minimum - args.input.length} caractère(s)`,
    loadingMore: () => 'Chargement de résultats supplémentaires...',
    removeAllItems: () => 'Supprimer tous les éléments'
  }
};

// Configuration pour les selects simples (non-multiples)
const select2SingleOptions = {
  ...select2Options,
  closeOnSelect: true // Fermer automatiquement après sélection
};

// Variable déjà déclarée plus haut

// Fonction pour initialiser Select2
const initializeSelect2 = () => {
  // Vérifier que jQuery et Select2 sont disponibles
  if (!window.jQuery || !$.fn.select2) {
    console.error('jQuery ou Select2 ne sont pas disponibles!');
    return;
  }
  
  // Détruire proprement les instances existantes si elles existent
  const selectIds = ['#shifts','#direction', '#grade', '#fonction', '#ville', '#statut'];
  
  // Détruire les instances existantes
  selectIds.forEach(id => {
    if ($(id).data('select2')) {
      $(id).select2('destroy');
    }
  });

  // Configuration commune pour tous les select2
  const commonConfig = {
    width: '100%',
    allowClear: true,
    closeOnSelect: false,
    language: {
      noResults: () => 'Aucun résultat trouvé',
      searching: () => 'Recherche en cours...',
      inputTooShort: (args) => `Veuillez saisir au moins ${args.minimum - args.input.length} caractère(s)`,
      loadingMore: () => 'Chargement de résultats supplémentaires...',
      removeAllItems: () => 'Supprimer tous les éléments'
    },
    escapeMarkup: (markup) => markup
  };

  // L'initialisation de Select2 pour les shifts a été supprimée
  
  // Appliquer des styles personnalisés aux dropdowns Select2
  setTimeout(() => {
    $('.select2-container--default .select2-selection--multiple').css({
      'border-color': '#5DD6C7',
      'border-radius': '8px'
    });
    
    $('.select2-container--default.select2-container--focus .select2-selection--multiple').css({
      'border-color': '#30C7B5',
      'box-shadow': '0 0 0 0.2rem rgba(48, 199, 181, 0.25)'
    });
    
    // Personnaliser les tags
    $('.select2-container--default .select2-selection--multiple .select2-selection__choice').css({
      'background-color': '#E0F7F4',
      'border-color': '#5DD6C7',
      'color': '#155751',
      'border-radius': '4px',
      'padding': '3px 8px'
    });
    
    // Personnaliser le bouton de suppression dans les tags
    $('.select2-container--default .select2-selection--multiple .select2-selection__choice__remove').css({
      'color': '#26A194',
      'margin-right': '5px'
    });
    
    $('.select2-container--default .select2-selection--multiple .select2-selection__choice__remove:hover').css({
      'color': '#FF7A68'
    });
  }, 100);
  
  // Initialiser Select2 pour les selects simples (non-multiples)
  $('#direction').select2({
    ...select2SingleOptions,
    placeholder: 'Sélectionner une direction',
    templateResult: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">business</span>${data.text}</span>`);
    },
    templateSelection: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">business</span>${data.text}</span>`);
    }
  }).on('change', function() {
    agent.direction_id = $(this).val();
  });
  
  $('#grade').select2({
    ...select2SingleOptions,
    placeholder: 'Sélectionner un grade',
    templateResult: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">military_tech</span>${data.text}</span>`);
    },
    templateSelection: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">military_tech</span>${data.text}</span>`);
    }
  }).on('change', function() {
    agent.grade_id = $(this).val();
  });
  
  $('#fonction').select2({
    ...select2SingleOptions,
    placeholder: 'Sélectionner une fonction',
    templateResult: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">work</span>${data.text}</span>`);
    },
    templateSelection: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">work</span>${data.text}</span>`);
    }
  }).on('change', function() {
    agent.fonction_id = $(this).val();
  });
  
  $('#ville').select2({
    ...select2SingleOptions,
    placeholder: 'Sélectionner une ville',
    templateResult: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">location_city</span>${data.text}</span>`);
    },
    templateSelection: (data) => {
      if (!data.id) return data.text;
      return $(`<span><span class="material-icons" style="font-size: 14px; vertical-align: middle; margin-right: 5px; color: #30C7B5;">location_city</span>${data.text}</span>`);
    }
  }).on('change', function() {
    agent.ville_id = $(this).val();
  });
  
  $('#statut').select2({
    ...select2SingleOptions,
    placeholder: 'Sélectionner un statut',
    templateResult: (data) => {
      if (!data.id) return data.text;
      let color = '#30C7B5';
      if (data.id === 'inactif') color = '#FF7A68';
      else if (data.id === 'conge') color = '#2B8FA6';
      else if (data.id === 'formation') color = '#27B577';
      else if (data.id === 'detachement') color = '#1D7A70';
      
      return $(`<span><span class="status-dot" style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 8px;"></span>${data.text}</span>`);
    },
    templateSelection: (data) => {
      if (!data.id) return data.text;
      let color = '#30C7B5';
      if (data.id === 'inactif') color = '#FF7A68';
      else if (data.id === 'conge') color = '#2B8FA6';
      else if (data.id === 'formation') color = '#27B577';
      else if (data.id === 'detachement') color = '#1D7A70';
      
      return $(`<span><span class="status-dot" style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 8px;"></span>${data.text}</span>`);
    }
  }).on('change', function() {
    agent.statut = $(this).val();
  });
  
  // La mise à jour des valeurs initiales de Select2 pour les shifts a été supprimée
  
  // Définir les valeurs initiales pour les selects simples
  if (agent.direction_id) {
    $('#direction').val(agent.direction_id).trigger('change');
  }
  
  if (agent.grade_id) {
    $('#grade').val(agent.grade_id).trigger('change');
  }
  
  if (agent.fonction_id) {
    $('#fonction').val(agent.fonction_id).trigger('change');
  }
  
  if (agent.ville_id) {
    $('#ville').val(agent.ville_id).trigger('change');
  }
  
  if (agent.statut) {
    $('#statut').val(agent.statut).trigger('change');
  }
  
  // Appliquer les styles OpSolver aux éléments Select2 une seule fois après l'initialisation
  setTimeout(() => {
    // Styles pour les conteneurs Select2 multiples
    $('.select2-container--default .select2-selection--multiple').css({
      'border-color': '#5DD6C7',
      'border-radius': '8px',
      'padding': '4px',
      'min-height': '38px'
    });
    
    // Styles pour les tags sélectionnés
    $('.select2-container--default .select2-selection--multiple .select2-selection__choice').css({
      'background-color': '#E0F7F4',
      'border-color': '#5DD6C7',
      'color': '#155751',
      'border-radius': '4px',
      'padding': '3px 8px',
      'margin': '2px 4px'
    });
    
    // Styles pour le bouton de suppression dans les tags
    $('.select2-container--default .select2-selection--multiple .select2-selection__choice__remove').css({
      'color': '#26A194',
      'margin-right': '5px',
      'font-weight': 'bold'
    });
    
    // Styles pour les selects simples (non-multiples)
    $('.select2-container--default .select2-selection--single').css({
      'border-color': '#5DD6C7',
      'border-radius': '8px',
      'height': '38px',
      'padding': '4px 8px'
    });
    
    // Style pour le texte sélectionné dans les selects simples
    $('.select2-container--default .select2-selection--single .select2-selection__rendered').css({
      'color': '#155751',
      'line-height': '28px'
    });
    
    // Style pour la flèche de déroulement dans les selects simples
    $('.select2-container--default .select2-selection--single .select2-selection__arrow').css({
      'height': '36px',
      'right': '8px'
    });
    
    // Colorier le bouton de la flèche dans les selects simples
    $('.select2-container--default .select2-selection--single .select2-selection__arrow b').css({
      'border-color': '#30C7B5 transparent transparent transparent',
      'border-width': '6px 4px 0 4px'
    });
  }, 100);
}

// Les fonctions de formatage sont maintenant intégrées directement dans les configurations de Select2

// Les watchers pour les shifts ont été supprimés

// Watchers pour les selects simples
watch(() => agent.direction_id, (newVal) => {
  if (select2Initialized.value && $('#direction').data('select2')) {
    $('#direction').val(newVal).trigger('change.select2');
  }
});

watch(() => agent.grade_id, (newVal) => {
  if (select2Initialized.value && $('#grade').data('select2')) {
    $('#grade').val(newVal).trigger('change.select2');
  }
});

watch(() => agent.fonction_id, (newVal) => {
  if (select2Initialized.value && $('#fonction').data('select2')) {
    $('#fonction').val(newVal).trigger('change.select2');
  }
});

watch(() => agent.ville_id, (newVal) => {
  if (select2Initialized.value && $('#ville').data('select2')) {
    $('#ville').val(newVal).trigger('change.select2');
  }
});

watch(() => agent.statut, (newVal) => {
  if (select2Initialized.value && $('#statut').data('select2')) {
    $('#statut').val(newVal).trigger('change.select2')
  }
})

// Le watcher pour les shifts a été supprimé

// Naviguer vers la vue précédente avec confirmation si nécessaire
const goBack = () => {
  // Vérifier si des modifications ont été apportées
  if (agent.id && (agent.matricule || agent.nom)) {
    // Afficher la boîte de dialogue de confirmation
    confirmCancelModal.value.showModal()
  } else {
    // Pas de modifications, retour direct
    router.push('/personnel')
  }
}

// Confirmer l'annulation et retourner à la liste
const confirmCancel = () => {
  confirmCancelModal.value.close()
  router.push('/personnel')
}

// Fermer la boîte de dialogue de confirmation d'annulation
const closeConfirmModal = () => {
  confirmCancelModal.value.close()
}

// Ouvrir la modale de confirmation de sauvegarde
const openConfirmSaveModal = () => {
  // Vérifier d'abord si le formulaire est valide
  if (!validateForm()) {
    return;
  }
  confirmSaveModal.value.showModal();
}

// Fermer la modale de confirmation de sauvegarde
const closeConfirmSaveModal = () => {
  confirmSaveModal.value.close();
}

// Confirmer la sauvegarde et exécuter l'enregistrement
const confirmSave = () => {
  confirmSaveModal.value.close();
  saveAgent();
}

// Gérer les erreurs de chargement d'image
const handleImageError = (event) => {
  // Remplacer par l'image par défaut en cas d'erreur
  event.target.src = require('@/assets/images/user-default.png');
}

// Fonction pour construire l'URL de la photo
const getPhotoUrl = (photoPath) => {
  const api_url = api.getApiUrl();
  
  if (!photoPath) {
    return require('@/assets/images/user-default.png');
  }
  
  // Si c'est une chaîne base64 (nouvelle photo sélectionnée ou capturée)
  if (typeof photoPath === 'string' && photoPath.startsWith('data:image')) {
    return photoPath;
  }
  
  // Si c'est un objet File (nouvelle photo sélectionnée)
  if (photoPath instanceof File) {
    return URL.createObjectURL(photoPath);
  }
  
  // Si c'est déjà une URL complète (http ou https)
  if (typeof photoPath === 'string' && photoPath.startsWith('http')) {
    return photoPath;
  }
  
  // Si c'est juste un nom de fichier, construire l'URL complète
  if (typeof photoPath === 'string' && !photoPath.startsWith('/')) {
    return `${api_url}${photoPath}`;
  }
  
  // Si c'est un chemin relatif commençant par /, ajouter le préfixe de l'API
  if (typeof photoPath === 'string') {
    return `${api_url}${photoPath}`;
  }
  
  // Cas par défaut
  return require('@/assets/images/user-default.png');
}

// Valider les champs obligatoires
const validateForm = () => {
  // Activer l'affichage des messages de validation
  showValidation.value = true
  
  // Liste des champs obligatoires
  const requiredFields = [
    { field: 'nom', label: 'Nom' },
    { field: 'sexe', label: 'Sexe' },
    { field: 'direction_id', label: 'Direction' },
    { field: 'adresse', label: 'Adresse' }
  ]
  
  // Vérifier chaque champ obligatoire
  const missingFields = requiredFields.filter(item => {
    // Vérification spéciale pour direction_id qui peut être une chaîne vide ou null
    if (item.field === 'direction_id') {
      return !agent[item.field] || agent[item.field] === ''
    }
    // Vérification pour adresse (chaîne vide ou uniquement espaces)
    if (item.field === 'adresse') {
      const val = agent[item.field]
      return val === undefined || val === null || (typeof val === 'string' && !val.trim())
    }
    return !agent[item.field]
  })
  
  if (missingFields.length > 0) {
    // Afficher un message d'erreur à l'utilisateur
    const missingFieldLabels = missingFields.map(f => f.label).join(', ')
    showToast({
      title: 'Erreur de validation',
      message: `Veuillez remplir les champs obligatoires suivants : ${missingFieldLabels}`,
      type: 'error'
    })
    
    // Défiler vers le premier champ manquant pour le rendre visible
    const fieldId = missingFields[0].field === 'direction_id' ? 'direction' : missingFields[0].field
    const firstMissingField = document.getElementById(fieldId)
    if (firstMissingField) {
      firstMissingField.scrollIntoView({ behavior: 'smooth', block: 'center' })
      firstMissingField.focus()
    }
    return false
  }
  
  return true
}

// Enregistrer les modifications
const saveAgent = async () => {
 
  
  // Valider le formulaire
  if (!validateForm()) {
    console.log('Validation du formulaire échouée')
    return
  }
  
  console.log('Validation du formulaire réussie, préparation des données à envoyer')
  
  try {
    // Afficher un indicateur de chargement
    loading.value = true
    
    // Créer un objet FormData pour envoyer les données et le fichier
    const formData = new FormData()
    
    // Liste des champs à envoyer au backend (correspondant à la structure de la base de données)
    const fieldsToSend = [
      'id', 'matricule', 'nom', 'sexe', 'date_naissance', 'lieu_naissance',
      'grade_id', 'fonction_id', 'direction_id', 'date_recrutement',
      'email', 'telephone', 'adresse', 'code_postal', 'ville_id',
      'default_shift_id', 'statut', 'active', 'badge_id'
    ];
    
    // Champs d'informations sociales (toujours envoyés au même endpoint création/mise à jour)
    const socialFields = [
      'etat_civil',
      'nom_conjoint',
      'nombre_enfants',
      'contact_conjoint',
      'contact_urgence'
    ];
    
    // N'ajouter que les champs nécessaires au FormData
    fieldsToSend.forEach(field => {
      if (agent[field] !== undefined && agent[field] !== null && field !== 'photo') {
        formData.append(field, agent[field]);
      }
    });
    
    // Informations sociales : toujours envoyer les clés au backend (même endpoint POST/PUT personnel)
    socialFields.forEach(field => {
      const value = agent[field];
      if (value !== undefined && value !== null) {
        formData.append(field, value);
      } else {
        formData.append(field, field === 'nombre_enfants' ? '0' : '');
      }
    });
    
    // noms_enfants : toujours envoyé en JSON (tableau, éventuellement vide)
    formData.append('noms_enfants', JSON.stringify(Array.isArray(agent.noms_enfants) ? agent.noms_enfants : []));
    
    // Le traitement des shifts a été supprimé
    
    // Traitement de la photo
    if (agent.photo) {
      try {
        // Si la photo est une URL ou un chemin existant (pas modifiée)
        if (typeof agent.photo === 'string' && (agent.photo.startsWith('http') || agent.photo.startsWith('/'))) {
          // Ajouter l'URL de la photo existante au FormData
          formData.append('photo', agent.photo)
          console.log('Photo existante conservée:', agent.photo)
        } 
        // Si la photo est une chaîne base64 (nouvelle photo ou modifiée)
        else if (typeof agent.photo === 'string' && agent.photo.startsWith('data:image')) {
          // Extraire le type MIME de l'image
          const mimeType = agent.photo.split(';')[0].split(':')[1];
          const extension = mimeType === 'image/png' ? 'png' : 'jpg';
          
          // Convertir la chaîne base64 en Blob
          const base64Response = await fetch(agent.photo)
          const blob = await base64Response.blob()
          
          // Ajouter le Blob au FormData avec le bon type MIME
          formData.append('photo', blob, `photo.${extension}`)
          console.log(`Nouvelle photo ajoutée au FormData (${mimeType})`)
        }
        // Si la photo est déjà un objet File
        else if (agent.photo instanceof File) {
          formData.append('photo', agent.photo)
          console.log('Objet File photo ajouté au FormData:', agent.photo.name)
        }
        // Cas non prévu
        else {
          console.warn('Format de photo non reconnu, la photo ne sera pas envoyée', typeof agent.photo)
        }
      } catch (photoError) {
        console.error('Erreur lors du traitement de la photo:', photoError)
        // Continuer sans la photo plutôt que d'abandonner toute la sauvegarde
        showToast({ 
          title: 'Attention', 
          message: "La photo n'a pas pu être traitée correctement et ne sera pas enregistrée", 
          type: 'warning' 
        })
      }
    }
    
    let response
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
    
    // Mode édition
    if (agent.num_personnel) {
      console.log(`Mise à jour de l'agent avec ID: ${agent.num_personnel}`)
      response = await api.put(`/api/personnel/${agent.num_personnel}`, formData, config)
      console.log('Réponse API (mise à jour):', response)
      showToast({ title: 'Succès', message: 'Agent mis à jour avec succès', type: 'success' })
    } 
    // Mode création
    else {
      console.log('Création d\'un nouvel agent')
      response = await api.post('/api/personnel', formData, config)
      console.log('Réponse API (création):', response)
      showToast({ title: 'Succès', message: 'Nouvel agent créé avec succès', type: 'success' })
    }
    
    // Mettre à jour le store
    console.log('Mise à jour du store personnel')
    await personnelStore.fetchPersonnel()
    
    // Rediriger vers la liste
    console.log('Redirection vers la liste du personnel')
    router.push('/personnel')
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement de l\'agent', error)
    
    // Analyse détaillée de l'erreur pour donner un message plus précis
    let errorMessage = "Une erreur est survenue lors de l'enregistrement";
    
    if (error.response) {
      // Erreur de réponse du serveur
      const status = error.response.status;
      const serverMessage = error.response.data?.message || '';
      
      if (status === 413) {
        errorMessage = "La photo est trop volumineuse. Veuillez utiliser une image plus petite.";
      } else if (status === 415) {
        errorMessage = "Le format de la photo n'est pas supporté. Utilisez JPG, PNG ou GIF.";
      } else if (status === 422) {
        errorMessage = `Données invalides: ${serverMessage}`;
      } else if (status >= 500) {
        errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
      } else {
        errorMessage = serverMessage || `Erreur (${status})`;
      }
    } else if (error.request) {
      // Pas de réponse reçue du serveur
      errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
    } else {
      // Erreur lors de la configuration de la requête
      errorMessage = error.message || "Erreur lors de la préparation de la requête";
    }
    
    showToast({ 
      title: 'Erreur', 
      message: errorMessage, 
      type: 'error' 
    })
  } finally {
    loading.value = false
  }
}

// Fonction pour ouvrir la modale de photo
const openPhotoModal = () => {
  // Si l'agent a déjà une photo, on l'utilise comme preview
  if (agent.photo) {
    previewPhoto.value = agent.photo;
  }
  photoModal.value.showModal();
};

// Fonction pour fermer la modale de photo
const closePhotoModal = () => {
  // Arrêter la webcam si elle est active
  if (showCamera.value) {
    stopWebcam();
    showCamera.value = false;
    cameraCaptured.value = false;
  }
  
  photoModal.value.close();
  
  // Réinitialiser la preview si on annule et qu'il n'y a pas de photo existante
  if (!agent.photo) {
    previewPhoto.value = '';
  }
};

// Fonction pour confirmer la sélection de photo
const confirmPhoto = () => {
  if (previewPhoto.value) {
    agent.photo = previewPhoto.value;
  }
  closePhotoModal();
};

// Fonction pour déclencher l'input file depuis la modale
const triggerFileInputFromModal = () => {
  fileInput.value.click();
};

// Fonction pour déclencher l'input file (pour compatibilité avec le code existant)
const triggerFileInput = () => {
  openPhotoModal();
}

// Gestion de la sélection de fichier
const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  // Vérifier que c'est une image
  if (!file.type.startsWith('image/')) {
    // Fermer le modal de photo avant d'afficher l'erreur
    if (photoModal.value && photoModal.value.open) {
      closePhotoModal()
    }
    // Attendre un peu pour que le modal se ferme avant d'afficher le toast
    setTimeout(() => {
      showToast({
        title: 'Erreur',
        message: 'Veuillez sélectionner un fichier image',
        type: 'error'
      })
    }, 100)
    event.target.value = ''
    return
  }
  
  // Vérifier la taille du fichier (max 5 MB)
  const maxSize = 5 * 1024 * 1024 // 5 MB en octets
  if (file.size > maxSize) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
    // Fermer le modal de photo avant d'afficher l'erreur
    if (photoModal.value && photoModal.value.open) {
      closePhotoModal()
    }
    // Attendre un peu pour que le modal se ferme avant d'afficher le toast
    setTimeout(() => {
      showToast({
        title: 'Fichier trop volumineux',
        message: `La photo est trop grande (${fileSizeMB} MB). La taille maximale autorisée est de 5 MB. Veuillez sélectionner une image plus petite.`,
        type: 'error'
      })
    }, 100)
    event.target.value = ''
    return
  }
  
  // Mettre à jour la prévisualisation dans la modale
  const reader = new FileReader()
  reader.onload = (e) => {
    // Mettre à jour la prévisualisation
    previewPhoto.value = e.target.result
    
    // Si on veut utiliser l'éditeur de rognage, décommenter cette partie
    // et commenter la ligne previewPhoto.value = e.target.result ci-dessus
    /*
    cropperImage.value = e.target.result
    showCropper.value = true
    */
    
    // S'assurer que la modale est ouverte
    if (!photoModal.value.open) {
      photoModal.value.showModal()
    }
  }
  reader.readAsDataURL(file)
  
  // Réinitialiser l'input file pour permettre de sélectionner le même fichier plusieurs fois
  event.target.value = ''
}

// Gestion de la webcam
const startWebcam = async () => {
  // S'assurer que la modale est ouverte
  if (!photoModal.value.open) {
    photoModal.value.showModal()
  }
  
  // Activer la webcam
  showCamera.value = true
  cameraCaptured.value = false
  
  try {
    stream.value = await navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      } 
    })
    
    // Attendre que le DOM soit mis à jour avec l'élément vidéo
    await nextTick()
    
    if (video.value) {
      video.value.srcObject = stream.value
    }
  } catch (error) {
    console.error('Erreur d\'accès à la webcam:', error)
    alert('Impossible d\'accéder à la webcam. Veuillez vérifier vos paramètres.')
    showCamera.value = false
  }
}

const stopWebcam = () => {
  if (stream.value) {
    stream.value.getTracks().forEach(track => track.stop())
    stream.value = null
  }
}

// Capturer une photo depuis la webcam
const capturePhoto = () => {
  if (!video.value || !canvas.value) return
  
  const context = canvas.value.getContext('2d')
  canvas.value.width = video.value.videoWidth
  canvas.value.height = video.value.videoHeight
  context.drawImage(video.value, 0, 0, canvas.value.width, canvas.value.height)
  
  // Afficher la photo capturée
  const capturedImage = canvas.value.toDataURL('image/png')
  previewPhoto.value = capturedImage
  video.value.style.display = 'none'
  canvas.value.style.display = 'block'
  cameraCaptured.value = true
}

// Reprendre une photo
const retakePhoto = () => {
  if (!video.value || !canvas.value) return
  
  video.value.style.display = 'block'
  canvas.value.style.display = 'none'
  cameraCaptured.value = false
}

// Accepter la photo capturée
const acceptPhoto = () => {
  if (!canvas.value) return
  
  // Utiliser directement la photo capturée
  const capturedImage = canvas.value.toDataURL('image/png')
  previewPhoto.value = capturedImage
  
  // Désactiver la caméra et arrêter le flux vidéo
  showCamera.value = false
  stopWebcam()
  
  // Si on veut utiliser le rognage (optionnel)
  /*
  cropperImage.value = capturedImage
  showCropper.value = true
  */
}

// Rogner la photo
const rotateLeft = () => {
  if (cropperInstance) {
    cropperInstance.rotate(-90)
  }
}

const rotateRight = () => {
  if (cropperInstance) {
    cropperInstance.rotate(90)
  }
}

const applyCrop = () => {
  if (!cropperInstance) return
  
  const croppedCanvas = cropperInstance.getCroppedCanvas({
    width: 300,
    height: 400,
    minWidth: 100,
    minHeight: 100,
    maxWidth: 4096,
    maxHeight: 4096,
    fillColor: '#fff',
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high',
  })
  
  // Vérifier les proportions
  const width = croppedCanvas.width
  const height = croppedCanvas.height
  const ratio = width / height
  
  if (ratio > 0.85) {
    alert('L\'image n\'est pas au format portrait. Veuillez recadrer pour obtenir un format portrait.')
    return
  }
  
  // Convertir en base64 pour prévisualisation et stockage
  const base64data = croppedCanvas.toDataURL('image/jpeg')
  photoPreview.value = base64data
  agent.photo = base64data
  
  // Fermer le modal de rognage
  showCropper.value = false
}

const cancelCrop = () => {
  showCropper.value = false
}

// Initialiser Cropper quand l'image est prête à être rognée
const initCropper = () => {
  // Détruire l'instance existante si elle existe
  if (cropperInstance) {
    cropperInstance.destroy()
    cropperInstance = null
  }
  
  // Attendre que l'image soit chargée
  nextTick(() => {
    if (cropperImage.value && showCropper.value) {
      cropperInstance = new Cropper(cropperImage.value, {
        aspectRatio: 3 / 4,  // Format portrait
        viewMode: 1,         // Limite l'image dans le cadre
        guides: true,        // Afficher les guides
        center: true,        // Afficher le point central
        highlight: true,     // Zone à rogner surlignée
        background: false,   // Afficher un fond quadrillé
        autoCropArea: 0.8,   // Taille de la zone de découpe par défaut
        responsive: true,    // Redimensionnement responsive
        zoomable: true,      // Permettre le zoom
        movable: true        // Permettre le déplacement
      })
    }
  })
}

// Observer les changements de l'image et du mode cropper
watch([() => showCropper.value, () => cropperImage.value], ([newShowCropper, newImage]) => {
  if (newShowCropper && newImage) {
    // L'image est prête à être rognée, initialiser Cropper
    initCropper()
  } else {
    // Détruire l'instance si on quitte le mode rognage
    if (cropperInstance) {
      cropperInstance.destroy()
      cropperInstance = null
    }
  }
})

// Observer les changements du modal de la caméra
watch(showCamera, (newVal) => {
  if (newVal) {
    // Démarrer la webcam quand on ouvre le modal
    startWebcam()
  } else {
    // Arrêter la webcam quand on ferme le modal
    stopWebcam()
    cameraCaptured.value = false
  }
})
</script>

<style>
/* Import du CSS de Select2 */
@import 'https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/css/select2.min.css';

/* Styles pour Select2 avec la palette de couleurs OpSolver */
.select2-container--default .select2-results__option--highlighted[aria-selected] {
  background-color: #30C7B5 !important;
  color: white;
}

.select2-container--default .select2-selection--multiple .select2-selection__choice {
  background-color: #E0F7F4 !important;
  border: 1px solid #5DD6C7 !important;
  color: #155751 !important;
}

.select2-container--default .select2-selection--multiple .select2-selection__choice__remove {
  color: #155751 !important;
}

.select2-container--default .select2-selection--multiple .select2-selection__choice__remove:hover {
  color: #FF7A68 !important;
}

.select2-container--default.select2-container--focus .select2-selection--multiple {
  border-color: #30C7B5 !important;
  box-shadow: 0 0 0 0.2rem rgba(48, 199, 181, 0.25) !important;
}

.shift-color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
}

.info-icon {
  font-size: 16px;
  color: #30C7B5;
  margin-right: 5px;
  vertical-align: middle;
}

/* Animation pour l'icône de chargement */
.loading-icon {
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.agent-edit-container {
  padding: 20px;
}

/* Styles généraux du formulaire */
.agent-edit-view {
  padding-bottom: 2rem;
  font-family: 'Roboto', 'Arial', sans-serif;
  color: #37474f;
}

.edit-card {
  background-color: #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
}

/* En-tête du formulaire */
.edit-header {
  background: white;
  color: var(--turquoise-deep, #155751);
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1.5rem 1rem 0 1rem; /* Ajouter de l'espace autour de la barre */
  border-bottom: 2px solid var(--turquoise-primary, #30C7B5);
  border-radius: 8px 8px 0 0; /* Arrondir les coins supérieurs */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); /* Ombre subtile */
}

.edit-header.new-mode {
  border-bottom: 2px solid var(--coral-soft, #FF7A68);
  color: #e05a48;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.edit-title {
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
}

.edit-subtitle {
  font-size: 1rem;
  opacity: 0.8;
  margin: 0;
}

.back-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 30px;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Corps du formulaire */
.edit-body {
  padding: 2rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--turquoise-primary);
  border-radius: 50%;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 1rem;
  color: var(--text-secondary);
}

/* Structure du formulaire */
.form-layout {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 2rem;
}

/* Section photo */
.photo-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.photo-preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
  background-color: #f8f9fa;
}

.photo-container {
  width: 100%;
  height: 280px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  border: 3px solid var(--turquoise-pale);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
}

.photo-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: var(--bg-lighter);
}

.photo-placeholder .material-icons {
  font-size: 6rem;
  color: var(--turquoise-light);
  opacity: 0.6;
}

.agent-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.photo-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-light);
  background-color: var(--bg-light);
  color: var(--turquoise-dark);
  cursor: pointer;
  transition: all 0.2s;
}

.photo-action-btn:hover {
  background-color: var(--turquoise-pale);
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(32, 211, 208, 0.15);
}

.photo-hint {
  text-align: center;
  font-size: 0.8rem;
  color: #757575;
}

/* Contenu du formulaire */
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--turquoise-pale);
}

.section-icon {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-dark);
  padding: 8px;
  border-radius: 50%;
  margin-right: 12px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--turquoise-deep);
  margin: 0;
}

.form-row {
  display: flex;
  gap: 1.2rem;
  margin-bottom: 1rem;
}

.form-column {
  flex: 1;
}

.form-column.full-width {
  flex-basis: 100%;
}

.form-field {
  margin-bottom: 1rem;
}

.form-field label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  color: #546e7a;
}

.form-field label.required-field::after {
  content: ' *';
  color: #f44336;
  font-weight: bold;
}

.error-message {
  color: #f44336;
  font-size: 0.8rem;
  margin-top: 4px;
  display: block;
}

.is-invalid {
  border-color: #f44336 !important;
  background-color: rgba(244, 67, 54, 0.05) !important;
}

.form-field input,
.form-field select,
.form-field textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 1rem;
  color: #37474f;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-field select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.form-field .multi-select {
  height: auto;
  min-height: 120px;
  padding: 0.5rem;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.form-field .multi-select option {
  padding: 0.5rem;
  margin-bottom: 3px;
  border-radius: 3px;
  transition: background-color 0.2s ease;
}

.form-field .multi-select option:hover {
  background-color: var(--turquoise-pale, #E0F7F4);
}

.form-field .multi-select option:checked {
  background-color: var(--turquoise-primary, #30C7B5);
  color: white;
  font-weight: 500;
}

/* Ajouter un indicateur visuel pour les champs multi-select */
.form-field label + .multi-select {
  position: relative;
}

.field-hint {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
  font-style: italic;
}

.noms-enfants-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.noms-enfants-row input {
  width: 100%;
}

/* Styles de base pour les champs de formulaire */
.form-field select {
  margin-bottom: 0.5rem;
}

.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  border-color: var(--turquoise-primary);
  box-shadow: 0 0 0 3px rgba(32, 211, 208, 0.15);
  outline: none;
}

.form-field input::placeholder,
.form-field textarea::placeholder {
  color: #b0bec5;
}

.form-field textarea {
  resize: vertical;
  min-height: 80px;
}

/* Boutons d'action */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-cancel,
.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background-color: #f5f5f5;
  color: var(--text-primary);
  border: 1px solid var(--border-medium);
}

.btn-cancel:hover {
  background-color: #e0e0e0;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn-save {
  background: linear-gradient(135deg, var(--turquoise-primary), var(--turquoise-dark));
  color: white;
}

.btn-save:hover {
  background: linear-gradient(135deg, var(--turquoise-dark), var(--turquoise-deep));
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(32, 211, 208, 0.3);
}

/* Modaux */
.camera-modal,
.cropper-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.camera-modal-content,
.cropper-modal-content {
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
}

.camera-modal-header,
.cropper-modal-header {
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  background-color: #f9f9f9;
}

.camera-modal-header h3,
.cropper-modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #455a64;
}

.camera-modal-body,
.cropper-modal-body {
  padding: 1.5rem;
  flex: 1;
  overflow: auto;
}

.camera-container,
.cropper-container,
.photo-preview-container {
  width: 100%;
  margin-bottom: 1.5rem;
  text-align: center;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f8f9fa;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.camera-controls,
.cropper-controls {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.camera-btn,
.cropper-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 30px;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.camera-btn-primary,
.cropper-btn-primary {
  background-color: #2196f3;
  color: white;
}

.camera-btn-secondary,
.cropper-btn-secondary {
  background-color: #e0e0e0;
  color: #455a64;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  color: #757575;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #455a64;
}

video {
  width: 100%;
  max-height: 400px;
  background-color: #000;
  border-radius: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .form-layout {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .edit-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
    .back-button {
    align-self: flex-start;
  }
}

/* Styles pour le bouton de retour dans le Header */
.btn-return {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.btn-return:hover {
  background: linear-gradient(to right, var(--turquoise-deep), #26A194);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.btn-return:active {
  transform: translateY(1px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-return .material-icons {
  font-size: 18px;
}

/* Styles pour la boîte de dialogue de confirmation */
.confirm-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
}

/* Styles spécifiques pour la modale de photo */
.photo-modal {
  max-width: 600px;
  max-height: 90vh;
}

.confirm-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.confirm-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 500;
}

.modal-body {
  padding: 20px;
  background-color: white;
}

.scrollable-modal-body {
  max-height: 60vh;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #30C7B5 #f1f1f1;
}

.scrollable-modal-body::-webkit-scrollbar {
  width: 6px;
}

.scrollable-modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.scrollable-modal-body::-webkit-scrollbar-thumb {
  background: #30C7B5;
  border-radius: 10px;
}

.scrollable-modal-body::-webkit-scrollbar-thumb:hover {
  background: #26A194;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 15px 20px;
  background-color: #f8f9fa;
  gap: 10px;
}

.warning {
  color: #e74c3c;
  font-weight: 500;
  margin-top: 10px;
}

.btn-close {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-secondary {
  background-color: #e0e0e0;
  color: #333;
}

.btn-action {
  background: linear-gradient(135deg, #30C7B5, #26A194);
  color: white;
  border: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.btn-action:hover {
  background: linear-gradient(135deg, #26A194, #1D7A70);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-action-outline {
  background-color: transparent;
  color: #26A194;
  border: 1px solid #30C7B5;
  transition: all 0.3s ease;
}

.btn-action-outline:hover {
  background-color: #E0F7F4;
  border-color: #26A194;
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(48, 199, 181, 0.15);
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
}

.btn-danger:hover {
  background-color: #ff6a56;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Styles personnalisés pour Select2 */
.select2-container--default .select2-results__option--highlighted[aria-selected] {
  background-color: #30C7B5 !important;
  color: white;
}

.select2-container--default .select2-search--inline .select2-search__field:focus {
  outline: none;
}

.select2-container--default .select2-dropdown {
  border-color: #5DD6C7;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.select2-container--default .select2-results__option[aria-selected=true] {
  background-color: #E0F7F4;
  color: #155751;
}

.select2-container--default.select2-container--open .select2-selection--multiple {
  border-color: #30C7B5;
  box-shadow: 0 0 0 0.2rem rgba(48, 199, 181, 0.25);
}

.shift-color-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: middle;
}

/* Styles pour la liste des shifts */
.shifts-list {
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
}

.shifts-title {
  font-size: 1rem;
  color: #455a64;
  margin-bottom: 1rem;
  font-weight: 500;
}

.shift-card {
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 3px solid #30C7B5;
  transition: all 0.2s ease;
}

.shift-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.shift-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.shift-name {
  display: flex;
  align-items: center;
  font-size: 1rem;
}

.shift-code {
  color: #78909c;
  margin-left: 5px;
  font-size: 0.9rem;
}

.shift-hours {
  font-weight: 500;
  color: #455a64;
  background-color: #f5f5f5;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.9rem;
}

.shift-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 0.9rem;
}

.shift-info {
  display: flex;
  align-items: center;
  color: #607d8b;
}

.shift-info .material-icons {
  font-size: 16px;
  margin-right: 5px;
  color: #30C7B5;
}

.shift-status {
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: #e0e0e0;
  color: #757575;
}

.shift-status.active {
  background-color: #E0F7F4;
  color: #155751;
}

.shift-assignment {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e0e0e0;
  font-size: 0.85rem;
  color: #607d8b;
}

.assignment-date, .assignment-comment {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.assignment-date .material-icons, .assignment-comment .material-icons {
  font-size: 14px;
  margin-right: 5px;
  color: #FF7A68;
}

/* Styles pour le toggle switch */
.toggle-field {
  margin-top: 10px;
}

.toggle-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.toggle-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #155751;
  margin-bottom: 0;
}

.toggle-icon {
  margin-right: 10px;
  color: #30C7B5;
}

.toggle-switch-container {
  display: flex;
  align-items: center;
}

.toggle-status {
  margin-left: 10px;
  font-weight: 500;
  min-width: 80px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #30C7B5;
}

input:focus + .slider {
  box-shadow: 0 0 1px #30C7B5;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

.toggle-hint {
  margin-top: 8px;
  display: flex;
  align-items: flex-start;
  padding-left: 15px;
}

.info-icon {
  color: #FF7A68;
  font-size: 18px;
  margin-right: 8px;
  flex-shrink: 0;
}

/* Styles pour la modale de photo */
.photo-modal {
  max-width: 600px;
  max-height: 90vh;
}

.photo-options {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.modal-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 250px;
  background-color: #f8f9fa;
  border-radius: 8px;
  color: #90a4ae;
}

.modal-placeholder .material-icons {
  font-size: 64px;
  margin-bottom: 1rem;
  color: #b0bec5;
}

.modal-placeholder p {
  font-size: 1rem;
  margin: 0;
}

.photo-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, #1D7A70, #30C7B5);
}

.photo-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.photo-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 250px;
  margin-bottom: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.photo-preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.modal-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  color: #999;
}

.modal-placeholder .material-icons {
  font-size: 64px;
  margin-bottom: 10px;
  color: #ccc;
}

.photo-options {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Styles pour la webcam */
.webcam-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

/* Uniformisation de la hauteur des champs */
.form-field input,
.form-field select,
.form-field .select2-container .select2-selection--single,
.form-field .select2-container .select2-selection--multiple {
  height: 38px !important;
  line-height: 38px;
}

.form-field .select2-container--default .select2-selection--single .select2-selection__rendered {
  line-height: 38px;
}

.form-field .select2-container--default .select2-selection--single .select2-selection__arrow {
  height: 36px;
}

.form-field textarea {
  min-height: 38px;
}

.webcam-video, .webcam-canvas {
  width: 100%;
  max-height: 350px;
  background-color: #000;
  border-radius: 8px;
  margin-bottom: 15px;
}

.webcam-controls {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
}

.webcam-controls .btn {
  display: flex;
  align-items: center;
  gap: 5px;
}

.webcam-controls .material-icons {
  font-size: 18px;
}

/* Les styles spécifiques aux shifts ont été supprimés */
</style>
